import { useEffect, useId, useRef } from 'react'
import type { PreppyMood } from './types'
import './preppy.css'

/**
 * Preppy — one character, several placements (launcher, panel header, flyer).
 *
 * A round-headed bot: a circular head, a pale swoop across the crown that runs
 * out into an antenna with a bead on the end, and oversized round glasses. The
 * glasses sit right and low of centre — that offset is what gives the
 * three-quarter look, so it is deliberate and not a bug.
 *
 * Class names are the contract with preppy.css: mood drives .preppy-lean,
 * .preppy-pupil, .preppy-shut and friends; .preppy-gaze takes the cursor.
 */

/* ── cursor tracking ─────────────────────────────────────────────────────
   One listener for every Preppy on screen rather than one each, throttled to
   a frame, writing straight to the DOM. Gaze must not re-render React — it
   fires on every mouse move and the panel underneath is not cheap. */

type GazeSubscriber = (x: number, y: number) => void

const subscribers = new Set<GazeSubscriber>()
let pointer = { x: 0, y: 0 }
let frame = 0
let listening = false

function flush() {
  frame = 0
  subscribers.forEach((fn) => fn(pointer.x, pointer.y))
}

function handlePointerMove(event: MouseEvent) {
  pointer = { x: event.clientX, y: event.clientY }
  if (frame) return
  frame = requestAnimationFrame(flush)
}

function subscribeToPointer(fn: GazeSubscriber) {
  subscribers.add(fn)
  if (!listening) {
    window.addEventListener('mousemove', handlePointerMove, { passive: true })
    listening = true
  }
  return () => {
    subscribers.delete(fn)
    if (subscribers.size === 0 && listening) {
      window.removeEventListener('mousemove', handlePointerMove)
      listening = false
      if (frame) {
        cancelAnimationFrame(frame)
        frame = 0
      }
    }
  }
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const MAX_GAZE_X = 2.2
const MAX_GAZE_Y = 1.8

type PreppyProps = {
  mood?: PreppyMood
  /** Rendered width and height in px. */
  size?: number
  /** Flips the lean when pointing, so Preppy leans towards what it points at. */
  facing?: 'left' | 'right'
  className?: string
}

export function Preppy({ mood = 'idle', size = 42, facing = 'right', className }: PreppyProps) {
  // useId keeps the clipPath unique across placements. The mockup hand-rolled a
  // counter for this; React already solves it, and correctly under StrictMode.
  const clipId = `preppy-head-${useId().replace(/:/g, '')}`
  const rootRef = useRef<SVGSVGElement>(null)
  const gazeRef = useRef<SVGGElement>(null)

  useEffect(() => {
    // Cursor tracking is continuous motion, so it goes when motion is reduced.
    if (prefersReducedMotion()) return

    return subscribeToPointer((x, y) => {
      const node = rootRef.current
      const gaze = gazeRef.current
      if (!node || !gaze) return

      const rect = node.getBoundingClientRect()
      if (!rect.width) return

      const dx = Math.max(-MAX_GAZE_X, Math.min(MAX_GAZE_X, (x - (rect.left + rect.width / 2)) / 30))
      const dy = Math.max(-MAX_GAZE_Y, Math.min(MAX_GAZE_Y, (y - (rect.top + rect.height / 2)) / 30))
      gaze.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`
    })
  }, [])

  return (
    <svg
      ref={rootRef}
      className={className ? `preppy ${className}` : 'preppy'}
      data-mood={mood}
      data-facing={facing}
      viewBox="0 0 56 56"
      width={size}
      height={size}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="26.5" cy="29.5" r="20.2" />
        </clipPath>
      </defs>

      <circle className="preppy-ring" cx="26.5" cy="29.5" r="23.4" />

      <g className="preppy-lean">
        <circle className="preppy-skin" cx="26.5" cy="29.5" r="20.2" />

        <g clipPath={`url(#${clipId})`}>
          <path
            className="preppy-hair"
            d="M8 37.8 C10.2 28.6 15.4 22.4 20.6 23.2 C24.6 23.8 26.4 25.4 30.4 25.9 C34.4 26.4 37.6 25.6 40.6 22.6 C43.2 20 45.2 17.4 46.3 15.4 L52 4 L2 4 L2 30 Z"
          />
        </g>

        <circle className="preppy-head" cx="26.5" cy="29.5" r="20.2" />

        {/* hairline and antenna are one unbroken stroke */}
        <path
          className="preppy-wave"
          d="M8 37.8 C10.2 28.6 15.4 22.4 20.6 23.2 C24.6 23.8 26.4 25.4 30.4 25.9 C34.4 26.4 37.6 25.6 40.6 22.6 C43.2 20 45.2 17.4 46.3 15.4"
        />
        <circle className="preppy-knob" cx="47.2" cy="14" r="2.9" />

        <circle className="preppy-lens" cx="23.5" cy="33.9" r="6.4" />
        <circle className="preppy-lens" cx="38.3" cy="33.9" r="6.4" />
        <path className="preppy-bridge" d="M29.7 36 Q30.9 37.6 32.1 36" />

        {/* Brows only appear when thinking, and they are mismatched on purpose:
            one arched high and thin, one low, straight and heavy. See preppy.css. */}
        <path className="preppy-brow preppy-brow--raised" d="M18.2 25.9 Q23.1 21.7 28.1 24.9" />
        <path className="preppy-brow preppy-brow--furrowed" d="M33.5 23.6 Q38 24.1 42.6 25.9" />

        <g className="preppy-pupils">
          <g className="preppy-gaze" ref={gazeRef}>
            <ellipse className="preppy-pupil" cx="23.95" cy="33.8" rx="2.95" ry="3.35" />
            <ellipse className="preppy-pupil" cx="38.75" cy="33.8" rx="2.95" ry="3.35" />
          </g>
        </g>

        <path className="preppy-smile" d="M27.8 40.9 Q31.25 44.4 34.7 41" />
        <path className="preppy-mouth-open" d="M27.2 40.2 H35.4 A4.1 4 0 0 1 27.2 40.2 Z" />
        <rect className="preppy-mouth-flat" x="28.2" y="40.2" width="6.3" height="2.2" rx="1.1" />
      </g>
    </svg>
  )
}
